import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Eye, X } from "lucide-react";
import toast from "react-hot-toast";
import EmployeeSalaryStructureDesigner from "./EmployeeSalaryStructureDesigner";
import EmployeeStructureHistory, {
  StructureHistoryTriggerButton,
} from "./EmployeeStructureHistory";
import { salaryStructureService } from "../../../../services/api";
import { templateToStructure } from "./utils/salaryStructurePersistence";
import {
  findApplicableSalaryStructure,
  normalizeEmployeeForStructureMatch,
} from "../../../../utils/salaryStructureMatch";
import { usePermissions } from "../../../../rbac/PermissionsContext";

/**
 * Eye icon next to CTC — preview/save salary structure breakdown for the matched structure.
 * Enabled only when CTC > 0 and org tags match an assigned structure
 * (branch / department / designation / category / etc. as linked on the template).
 * Preview works before the employee is created; Save requires employee.id.
 */
export default function EmployeeSalaryStructureModal({
  employee,
  darkMode = false,
  disabled = false,
  title = "View salary structure",
  orgSalaryMethod,
  /** Increment to force-open (e.g. after create employee). */
  autoOpenKey = 0,
  /** Hide the eye trigger when opening programmatically (e.g. Payroll Actions). */
  hideTrigger = false,
  onSaved,
}) {
  const [open, setOpen] = useState(false);
  const [matchedStructure, setMatchedStructure] = useState(null);
  const [structureReady, setStructureReady] = useState(false);
  const [checking, setChecking] = useState(false);
  const [footerActions, setFooterActions] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const lastAutoOpenKey = useRef(0);
  const pendingAutoOpenRef = useRef(false);
  /** Match key for which structure check last finished — prevents first-open race toast. */
  const completedMatchKeyRef = useRef(null);
  const templatesCacheRef = useRef(null);
  const { hasScreenAccess, isAdmin } = usePermissions();
  const canExportBreakdown =
    isAdmin || hasScreenAccess("payroll", "salary_structure", "export");
  const previewOnly = !employee?.id;
  const employeeForMatch = useMemo(
    () => normalizeEmployeeForStructureMatch(employee),
    [employee]
  );

  const hasCtc = Number(employeeForMatch?.ctc) > 0;
  const matchKey = useMemo(
    () =>
      [
        employeeForMatch?.id || "",
        employeeForMatch?.ctc || "",
        employeeForMatch?.branchId || "",
        employeeForMatch?.departmentId || "",
        employeeForMatch?.designationId || "",
        employeeForMatch?.gradeId || "",
        employeeForMatch?.categoryId || "",
        employeeForMatch?.subDepartmentId || "",
        employeeForMatch?.locationId || employeeForMatch?.workLocationId || "",
        employeeForMatch?.employmentType || "",
        employeeForMatch?.pfApplicable !== false ? "1" : "0",
        employeeForMatch?.esiApplicable !== false ? "1" : "0",
        employeeForMatch?.ptApplicable !== false ? "1" : "0",
        employeeForMatch?.gratuityApplicable !== false ? "1" : "0",
        employeeForMatch?.ceilingPf !== false ? "1" : "0",
      ].join("|"),
    [employeeForMatch]
  );

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") {
        if (showHistory) {
          setShowHistory(false);
          return;
        }
        setOpen(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, showHistory]);

  // Pre-check: enable eye only when a structure matches the linked org tags.
  useEffect(() => {
    let cancelled = false;
    if (disabled || !hasCtc) {
      completedMatchKeyRef.current = matchKey;
      setStructureReady(false);
      setMatchedStructure(null);
      setChecking(false);
      return undefined;
    }

    // Mark in-progress immediately so auto-open does not toast on first paint.
    setChecking(true);
    completedMatchKeyRef.current = null;

    (async () => {
      try {
        // Always refresh when rematching — new structures created in Settings must be visible
        const res = await salaryStructureService.listTemplates();
        const rows = (Array.isArray(res?.data) ? res.data : [])
          .map(templateToStructure)
          .filter(Boolean);
        templatesCacheRef.current = rows;
        if (cancelled) return;
        const matched = findApplicableSalaryStructure(employeeForMatch, rows);
        setMatchedStructure(matched || null);
        setStructureReady(!!matched);
      } catch {
        if (!cancelled) {
          setMatchedStructure(null);
          setStructureReady(false);
        }
      } finally {
        if (!cancelled) {
          completedMatchKeyRef.current = matchKey;
          setChecking(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- rematch when org/statutory tags change
  }, [disabled, hasCtc, matchKey]);

  const employeeName =
    [employee?.user?.name, employee?.user?.lastName].filter(Boolean).join(" ").trim() ||
    [employee?.firstName, employee?.lastName].filter(Boolean).join(" ").trim() ||
    employee?.name ||
    employee?.fullName ||
    employee?.employeeName ||
    "";
  const amountLabel = employeeForMatch?.ctc
    ? `₹${Number(employeeForMatch.ctc).toLocaleString("en-IN")}`
    : "Not set";

  const eyeDisabled = disabled || checking || !hasCtc || !structureReady;

  const eyeTitle = (() => {
    if (disabled) return "Salary structure unavailable";
    if (checking) return "Checking structure assignment…";
    if (!hasCtc) return "Enter profile amount (CTC/Gross) first";
    if (!structureReady) {
      return "No salary structure matches this employee’s org tags and employment type. Assign or adjust a structure under Employee → Settings → Structures.";
    }
    return previewOnly
      ? "Preview salary breakdown for the assigned structure"
      : title;
  })();

  const openWithMatch = async ({ silent = false } = {}) => {
    if (eyeDisabled && !silent) {
      if (!hasCtc) {
        toast.error("Enter profile amount first to view salary breakdown.");
      } else if (!structureReady) {
        toast.error(
          "Salary structure not assigned for this employee. Assign a structure under Employee → Settings → Structures.",
          { duration: 6000 }
        );
      }
      return false;
    }
    if (!structureReady || !matchedStructure) return false;
    setOpen(true);
    return true;
  };

  const handleOpen = () => {
    openWithMatch({ silent: false });
  };

  useEffect(() => {
    if (!autoOpenKey || autoOpenKey === lastAutoOpenKey.current) return;
    if (!employee?.id) return;
    lastAutoOpenKey.current = autoOpenKey;
    pendingAutoOpenRef.current = true;
  }, [autoOpenKey, employee?.id]);

  useEffect(() => {
    if (!pendingAutoOpenRef.current) return;
    if (!employee?.id) return;
    // Wait until structure match for *this* employee finishes (fixes first-open false negative).
    if (checking || completedMatchKeyRef.current !== matchKey) return;

    if (!structureReady || !matchedStructure) {
      pendingAutoOpenRef.current = false;
      if (hideTrigger) {
        if (!hasCtc) {
          toast.error("Enter profile amount first to view salary breakdown.");
        } else {
          toast.error(
            "Salary structure not assigned for this employee. Assign a structure under Employee → Settings → Structures.",
            { duration: 6000 }
          );
        }
      }
      return;
    }
    pendingAutoOpenRef.current = false;
    setOpen(true);
  }, [
    employee?.id,
    structureReady,
    matchedStructure,
    checking,
    hideTrigger,
    hasCtc,
    matchKey,
  ]);

  return (
    <>
      {!hideTrigger && (
        <button
          type="button"
          onClick={handleOpen}
          disabled={eyeDisabled}
          className={`inline-flex items-center justify-center p-1 transition-colors disabled:cursor-not-allowed disabled:opacity-40 ${
            darkMode
              ? "text-[hsl(var(--ds-muted-fg))] hover:text-[hsl(var(--ds-accent-fg))]"
              : "text-[hsl(var(--ds-muted-fg))] hover:text-[hsl(var(--ds-primary))]"
          }`}
          title={eyeTitle}
          aria-label={title}
        >
          <Eye size={16} strokeWidth={2} />
        </button>
      )}

      {open &&
        createPortal(
          <div
            className="fixed inset-0 z-[10007] flex items-center justify-center bg-black/45 p-4"
            onClick={() => {
              setShowHistory(false);
              setOpen(false);
            }}
          >
            <div
              role="dialog"
              aria-modal="true"
              aria-labelledby="salary-structure-modal-title"
              className={`flex max-h-[92vh] w-[min(100%,820px)] flex-col overflow-hidden rounded-xl border shadow-2xl ${
                darkMode
                  ? "border-[hsl(var(--ds-border))] bg-[#1a1a1a]"
                  : "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))]"
              }`}
              onClick={(e) => e.stopPropagation()}
            >
              <div
                className={`flex items-start justify-between gap-3 border-b px-5 py-4 ${
                  darkMode ? "border-[hsl(var(--ds-border))]" : "border-[hsl(var(--ds-border))]"
                }`}
              >
                <div>
                  <h4
                    id="salary-structure-modal-title"
                    className={`text-base font-semibold ${
                      darkMode ? "text-gray-100" : "text-[hsl(var(--ds-fg))]"
                    }`}
                  >
                    Salary Structure
                    {previewOnly ? " (Preview)" : ""}
                  </h4>
                  <p
                    className={`mt-0.5 text-xs ${
                      darkMode ? "text-[hsl(var(--ds-muted-fg))]" : "text-[hsl(var(--ds-muted-fg))]"
                    }`}
                  >
                    {employeeName || "New employee"}
                    {employee?.employeeCode || employee?.empCode
                      ? ` (${employee.employeeCode || employee.empCode})`
                      : ""}
                    {" · "}Profile amount: {amountLabel}
                    {matchedStructure?.name ? ` · ${matchedStructure.name}` : ""}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setShowHistory(false);
                    setOpen(false);
                  }}
                  className={`rounded-full p-1 transition-colors ${
                    darkMode
                      ? "text-[hsl(var(--ds-muted-fg))] hover:bg-[#2C2C2C] hover:text-gray-200"
                      : "text-[hsl(var(--ds-muted-fg))] hover:bg-[hsl(var(--ds-muted))] hover:text-[hsl(var(--ds-fg))]"
                  }`}
                  aria-label="Close"
                >
                  <X size={18} />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto px-5 py-4">
                <EmployeeSalaryStructureDesigner
                  employee={employeeForMatch}
                  darkMode={darkMode}
                  matchedStructure={matchedStructure}
                  orgSalaryMethod={orgSalaryMethod}
                  onActionsChange={setFooterActions}
                />
              </div>

              <div
                className={`flex flex-wrap items-center justify-between gap-2 border-t px-5 py-3 ${
                  darkMode ? "border-[hsl(var(--ds-border))]" : "border-[hsl(var(--ds-border))]"
                }`}
              >
                <div className="flex flex-wrap items-center gap-2">
                  {!previewOnly && employee?.id ? (
                    <StructureHistoryTriggerButton
                      darkMode={darkMode}
                      onClick={() => setShowHistory(true)}
                    />
                  ) : null}
                  {canExportBreakdown ? (
                    <>
                      <button
                        type="button"
                        onClick={() => footerActions?.exportExcel?.()}
                        disabled={!footerActions?.canExport || footerActions?.exporting}
                        className={`rounded-lg px-3 py-2 text-sm transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
                          darkMode
                            ? "border border-[#4C4C5C] bg-[#2C2C2C] text-gray-200 hover:bg-[hsl(var(--ds-surface))]"
                            : "border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] text-[hsl(var(--ds-fg))] hover:bg-[hsl(var(--ds-muted))]"
                        }`}
                      >
                        {footerActions?.exporting ? "Exporting…" : "Export Excel"}
                      </button>
                      <button
                        type="button"
                        onClick={() => footerActions?.exportPdf?.()}
                        disabled={!footerActions?.canExport || footerActions?.exporting}
                        className={`rounded-lg px-3 py-2 text-sm transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
                          darkMode
                            ? "border border-[#4C4C5C] bg-[#2C2C2C] text-gray-200 hover:bg-[hsl(var(--ds-surface))]"
                            : "border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] text-[hsl(var(--ds-fg))] hover:bg-[hsl(var(--ds-muted))]"
                        }`}
                      >
                        Export PDF
                      </button>
                    </>
                  ) : null}
                  {footerActions?.saveMsg ? (
                    <p
                      className={`text-xs ${
                        darkMode ? "text-[hsl(var(--ds-muted-fg))]" : "text-[hsl(var(--ds-muted-fg))]"
                      }`}
                    >
                      {footerActions.saveMsg}
                    </p>
                  ) : null}
                </div>
                <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowHistory(false);
                    setOpen(false);
                  }}
                  className={`rounded-lg px-4 py-2 text-sm transition-colors ${
                    darkMode
                      ? "bg-[#2C2C2C] text-gray-200 hover:bg-[hsl(var(--ds-surface))]"
                      : "border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] text-[hsl(var(--ds-fg))] hover:bg-[hsl(var(--ds-muted))]"
                  }`}
                >
                  {previewOnly ? "Close" : "Cancel"}
                </button>
                  <button
                    type="button"
                    onClick={async () => {
                      if (previewOnly) {
                        toast.error(
                          "Click Onboard Employee to create the record and apply this structure."
                        );
                        return;
                      }
                      if (!footerActions?.save) return;
                      const ok = await footerActions.save();
                      if (ok) {
                        setOpen(false);
                        try {
                          onSaved?.();
                        } catch {
                          /* ignore */
                        }
                      }
                    }}
                    disabled={
                      previewOnly || !footerActions?.canSave || footerActions?.saving
                    }
                    title={
                      previewOnly
                        ? "Open this employee from Employees list after onboard — Save needs employee id"
                        : footerActions?.saveDisabledReason ||
                          (!footerActions?.canSave ? "Save not available yet" : undefined)
                    }
                    className="rounded-lg bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {footerActions?.saving ? "Saving…" : "Save"}
                  </button>
                </div>
              </div>
            </div>
          </div>,
          document.body
        )}

      <EmployeeStructureHistory
        isOpen={showHistory}
        onClose={() => setShowHistory(false)}
        employeeId={employee?.id}
        employeeName={employeeName}
        employeeCode={employee?.employeeCode || employee?.empCode || ""}
        darkMode={darkMode}
        onEditActive={() => {
          /* Active structure is already loaded in this modal */
        }}
        onStructureChanged={() => {
          try {
            onSaved?.();
          } catch {
            /* ignore */
          }
        }}
      />
    </>
  );
}
